import { createFeature, createReducer, on } from '@ngrx/store';

import { OktaUserListItem, OktaUserMfa } from '../services/okta-user';
import { OktaUsersActions } from './okta-users.actions';

/** Cached pages and MFA entries are refetched once older than this. */
export const OKTA_USERS_CACHE_TTL_MS = 5 * 60 * 1000;

export interface CachedOktaUsersPage {
  users: OktaUserListItem[];
  nextCursor: string | null;
  fetchedAt: number;
}

export interface CachedOktaUserMfa {
  mfa: OktaUserMfa;
  fetchedAt: number;
}

export interface RequestStatus {
  loading: boolean;
  error: string | null;
}

export interface OktaUsersState {
  pages: Record<string, CachedOktaUsersPage>;
  pageStatus: Record<string, RequestStatus>;
  mfaByUserId: Record<string, CachedOktaUserMfa>;
  mfaStatus: Record<string, RequestStatus>;
}

const initialState: OktaUsersState = {
  pages: {},
  pageStatus: {},
  mfaByUserId: {},
  mfaStatus: {},
};

/**
 * Cache key for a list page. The list no longer requests MFA (see
 * OktaUserService.getUsersPage), so the `after` cursor alone identifies a page.
 */
export const pageKey = (after: string | null | undefined): string =>
  after ?? '';

export const isFresh = (
  entry: { fetchedAt: number } | undefined,
  now: number,
): boolean => !!entry && now - entry.fetchedAt < OKTA_USERS_CACHE_TTL_MS;

const omit = <T>(record: Record<string, T>, key: string): Record<string, T> => {
  const { [key]: _removed, ...rest } = record;
  return rest;
};

const mapPageUsers = (
  pages: Record<string, CachedOktaUsersPage>,
  fn: (users: OktaUserListItem[]) => OktaUserListItem[],
): Record<string, CachedOktaUsersPage> =>
  Object.fromEntries(
    Object.entries(pages).map(([key, page]) => [
      key,
      { ...page, users: fn(page.users) },
    ]),
  );

export const oktaUsersFeature = createFeature({
  name: 'oktaUsers',
  reducer: createReducer(
    initialState,
    on(OktaUsersActions.loadPageStarted, (state, { after }) => ({
      ...state,
      pageStatus: {
        ...state.pageStatus,
        [pageKey(after)]: { loading: true, error: null },
      },
    })),
    on(
      OktaUsersActions.loadPageSuccess,
      (state, { after, page, fetchedAt }) => ({
        ...state,
        pages: {
          ...state.pages,
          [pageKey(after)]: {
            users: page.users,
            nextCursor: page.nextCursor,
            fetchedAt,
          },
        },
        pageStatus: {
          ...state.pageStatus,
          [pageKey(after)]: { loading: false, error: null },
        },
      }),
    ),
    on(OktaUsersActions.loadPageFailure, (state, { after, error }) => ({
      ...state,
      pageStatus: {
        ...state.pageStatus,
        [pageKey(after)]: { loading: false, error },
      },
    })),
    on(OktaUsersActions.pagesInvalidated, (state) => ({
      ...state,
      pages: {},
      pageStatus: {},
    })),
    on(OktaUsersActions.loadUserMfaStarted, (state, { userId }) => ({
      ...state,
      mfaStatus: {
        ...state.mfaStatus,
        [userId]: { loading: true, error: null },
      },
    })),
    on(
      OktaUsersActions.loadUserMfaSuccess,
      (state, { userId, mfa, fetchedAt }) => ({
        ...state,
        mfaByUserId: { ...state.mfaByUserId, [userId]: { mfa, fetchedAt } },
        mfaStatus: {
          ...state.mfaStatus,
          [userId]: { loading: false, error: null },
        },
      }),
    ),
    on(OktaUsersActions.loadUserMfaFailure, (state, { userId, error }) => ({
      ...state,
      mfaStatus: {
        ...state.mfaStatus,
        [userId]: { loading: false, error },
      },
    })),
    on(OktaUsersActions.userMfaInvalidated, (state, { userId }) => ({
      ...state,
      mfaByUserId: omit(state.mfaByUserId, userId),
      mfaStatus: omit(state.mfaStatus, userId),
    })),
    on(OktaUsersActions.userUpdated, (state, { userId, changes }) => ({
      ...state,
      pages: mapPageUsers(state.pages, (users) =>
        users.map((u) => (u.id === userId ? { ...u, ...changes } : u)),
      ),
      mfaByUserId: omit(state.mfaByUserId, userId),
      mfaStatus: omit(state.mfaStatus, userId),
    })),
    on(OktaUsersActions.userRemoved, (state, { userId }) => ({
      ...state,
      pages: mapPageUsers(state.pages, (users) =>
        users.filter((u) => u.id !== userId),
      ),
      mfaByUserId: omit(state.mfaByUserId, userId),
      mfaStatus: omit(state.mfaStatus, userId),
    })),
  ),
});
